<template>
  <div>
    <v-card class="chat" color="background" rounded="lg" variant="flat" elevation="2">
      <div class="chat-head bg-surface">
        <span class="chat-agent">{{ chat.agent }}</span>
        <span class="chat-thread text-medium-emphasis">{{ chat.thread }}</span>
        <span class="chat-sub text-disabled">{{ chat.sub }}</span>
      </div>
      <div class="chat-log">
        <template v-for="(entry, i) in chat.entries">
          <div
            v-if="entry.kind === 'message'"
            :key="'m' + i"
            class="msg"
            :class="entry.role === 'user' ? 'msg--user' : 'msg--bot'"
          >
            <div class="msg-role text-disabled">{{ entry.label }}</div>
            <!-- Static, author-controlled marketing copy (no user input) -->
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="msg-body" v-html="entry.html"></div>
          </div>

          <div v-else :key="'t' + i" class="chat-tools">
            <div class="chat-tools-head text-disabled">
              <span class="chat-tools-title">
                <v-icon icon="mdi-wrench-outline" size="13" class="text-primary" /> Tool activity
              </span>
              <span>{{ entry.note }}</span>
            </div>
            <ToolCallRow v-for="(call, ci) in entry.calls" :key="ci" :call="call" />
          </div>
        </template>
      </div>

      <div class="chat-outcome">
        <span class="chat-outcome-text">
          <v-icon icon="mdi-check-circle" size="18" class="chat-outcome-icon" />
          <strong>{{ outcome.headline }}</strong> — {{ outcome.detail }}
        </span>
        <span class="chat-outcome-meta text-disabled">{{ outcome.meta }}</span>
      </div>
    </v-card>
    <p class="chat-caption text-disabled">
      <v-icon icon="mdi-message-text-outline" size="15" class="chat-caption__icon" /> {{ caption }}
    </p>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import ToolCallRow from './ToolCallRow.vue'
import { configChat, configCaption, configOutcome } from './landingContent'

@Options({
  components: { ToolCallRow }
})
export default class ChatTranscript extends Vue {
  chat = configChat
  caption = configCaption
  outcome = configOutcome
}
</script>

<style scoped lang="scss">
.chat {
  margin-top: 40px;
  border: 1px solid var(--l-border);
  overflow: hidden;
}
.chat-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-bottom: 2px solid rgb(var(--v-theme-msq-yellow));
  flex-wrap: wrap;
}
.chat-agent {
  font-family: var(--l-mono);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.chat-thread {
  font-family: var(--l-mono);
  font-size: 12px;
}
.chat-sub {
  font-family: var(--l-mono);
  font-size: 11px;
  margin-left: auto;
}
.chat-log {
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.msg {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.msg-role {
  font-family: var(--l-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.msg--user {
  align-items: flex-end;
}
.msg--user .msg-body {
  background: rgb(var(--v-theme-surface));
  border: 1px solid var(--l-border);
  border-radius: 10px;
  padding: 13px 16px;
  max-width: 82%;
  font-size: 14px;
  line-height: 1.55;
}
.msg--bot .msg-body {
  max-width: 94%;
  font-size: 14px;
  line-height: 1.55;
}

/* tool-activity block (same grammar as the trace plate's detail panel) */
.chat-tools {
  background: rgb(var(--v-theme-theme-code));
  border: 1px solid var(--l-border);
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.chat-tools-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  font-family: var(--l-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}
.chat-tools-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* outcome strip (mirrors the design's run footer) */
.chat-outcome {
  padding: 14px 24px;
  background: rgb(var(--v-theme-theme-code));
  border-top: 1px solid var(--l-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  font-size: 13px;
}
.v-theme--light .chat-outcome {
  background: rgba(var(--v-theme-on-background), 0.04);
}
.chat-outcome-text {
  display: inline-flex;
  align-items: flex-start;
  gap: 10px;
}
.chat-outcome-icon {
  flex-shrink: 0;
  margin-top: 1px;
}
.chat-outcome-text strong {
  font-weight: 600;
}
.chat-outcome-icon {
  color: rgb(var(--v-theme-primary));
}
.v-theme--light .chat-outcome-icon {
  color: rgb(var(--v-theme-success));
}
.chat-outcome-meta {
  font-family: var(--l-mono);
  font-size: 12px;
  letter-spacing: 0.04em;
}

/* rich body content injected via v-html */
.msg-body :deep(p) {
  margin: 0 0 10px;
}
.msg-body :deep(p:last-child) {
  margin-bottom: 0;
}
.msg-body :deep(.plan-h) {
  font-weight: 600;
}
.msg-body :deep(.plan-agent) {
  margin-bottom: 10px;
}
.msg-body :deep(.plan-agent-name) {
  font-weight: 600;
  margin: 0 0 4px;
}
.msg-body :deep(ul) {
  margin: 0 0 10px;
  padding-left: 18px;
}
.msg-body :deep(li) {
  font-size: 13px;
  line-height: 1.5;
  color: rgba(var(--v-theme-on-surface), 0.68);
  margin-bottom: 3px;
}
.msg-body :deep(code) {
  font-family: var(--l-mono);
  font-size: 0.85em;
  background: rgb(var(--v-theme-theme-code));
  color: rgb(var(--v-theme-theme-on-code));
  padding: 1px 5px;
  border-radius: 4px;
}
.msg-body :deep(.plan-created) {
  list-style: none;
  padding-left: 0;
}
.msg-body :deep(.plan-created li) {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: rgb(var(--v-theme-on-surface));
}
.msg-body :deep(.plan-created .mdi) {
  color: rgb(var(--v-theme-accent-green));
  font-size: 15px;
  line-height: inherit;
}
.chat-caption {
  font-family: var(--l-mono);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin: 16px 0 0;
}
.chat-caption__icon {
  color: rgb(var(--v-theme-accent-ink));
}
@media (max-width: 900px) {
  .chat-sub {
    display: none;
  }
}
</style>
